import type {
  PrinterWithRelations,
  PrinterLibraryQuery,
  CreatePrinterLibraryInput,
  UpdatePrinterLibraryInput,
  TenantPrinterWithDetails,
  TenantPrinterQuery,
  AddPrinterToLibraryInput,
  UpdateTenantPrinterInput,
  PrinterBrand,
  PrinterModel,
  AccessoryType,
  CreatePrinterBrandInput,
  CreatePrinterModelInput,
  CreateAccessoryTypeInput,
  TenantPrinterAccessoryWithDetails,
  AddAccessoryToPrinterInput,
  UpdateAccessoryInput,
  TenantPrinterNote,
  CreatePrinterNoteInput,
  UpdatePrinterNoteInput,
  PrintWithRelations,
  PrintQuery,
  CreatePrintInput,
  UpdatePrintInput,
  PaginationMeta,
} from '@maker-toolchest/shared';
import { api } from '@/lib/api';

// Paginated result interface
export interface PaginatedResult<T> {
  items: T[];
  pagination: PaginationMeta;
}

// Helper to build query strings
function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(([_, v]) => v != null);
  if (entries.length === 0) return '';
  const searchParams = new URLSearchParams();
  entries.forEach(([k, v]) => searchParams.append(k, String(v)));
  return `?${searchParams.toString()}`;
}

// ============================================================================
// PRINTER LIBRARY API (Product Catalog)
// ============================================================================

export const printerLibraryApi = {
  /**
   * Search printer catalog (system + custom)
   */
  search: async (query?: PrinterLibraryQuery): Promise<PaginatedResult<PrinterWithRelations>> => {
    const response = await api.get<PrinterWithRelations[]>(`/printers/library${buildQueryString(query)}`);
    if (!response.success || !response.data || !response.meta?.pagination) {
      throw new Error('Failed to fetch printer library');
    }
    return {
      items: response.data,
      pagination: response.meta.pagination,
    };
  },

  /**
   * Get a single printer from catalog
   */
  getById: async (id: string): Promise<PrinterWithRelations> => {
    const response = await api.get<PrinterWithRelations>(`/printers/library/${id}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch printer');
    }
    return response.data;
  },

  /**
   * Create a custom printer in catalog
   */
  create: async (input: CreatePrinterLibraryInput): Promise<PrinterWithRelations> => {
    const response = await api.post<PrinterWithRelations>('/printers/library', input);
    if (!response.success || !response.data) {
      throw new Error('Failed to create printer');
    }
    return response.data;
  },

  /**
   * Update a custom printer in catalog
   */
  update: async (id: string, input: UpdatePrinterLibraryInput): Promise<PrinterWithRelations> => {
    const response = await api.patch<PrinterWithRelations>(`/printers/library/${id}`, input);
    if (!response.success || !response.data) {
      throw new Error('Failed to update printer');
    }
    return response.data;
  },

  /**
   * Delete a custom printer from catalog
   */
  delete: async (id: string): Promise<void> => {
    const response = await api.delete<void>(`/printers/library/${id}`);
    if (!response.success) {
      throw new Error('Failed to delete printer');
    }
  },
};

// ============================================================================
// TENANT PRINTER API (Owned Instances)
// ============================================================================

export const tenantPrinterApi = {
  /**
   * List tenant's owned printers
   */
  list: async (query?: TenantPrinterQuery): Promise<TenantPrinterWithDetails[]> => {
    const response = await api.get<TenantPrinterWithDetails[]>(`/printers${buildQueryString(query)}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch printers');
    }
    return response.data;
  },

  /**
   * Get a single tenant printer
   */
  getById: async (id: string): Promise<TenantPrinterWithDetails> => {
    const response = await api.get<TenantPrinterWithDetails>(`/printers/${id}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch printer');
    }
    return response.data;
  },

  /**
   * Add a printer from library to workspace
   */
  addToLibrary: async (input: AddPrinterToLibraryInput): Promise<TenantPrinterWithDetails> => {
    const response = await api.post<TenantPrinterWithDetails>('/printers/add', input);
    if (!response.success || !response.data) {
      throw new Error('Failed to add printer');
    }
    return response.data;
  },

  /**
   * Update a tenant printer
   */
  update: async (id: string, input: UpdateTenantPrinterInput): Promise<TenantPrinterWithDetails> => {
    const response = await api.patch<TenantPrinterWithDetails>(`/printers/${id}`, input);
    if (!response.success || !response.data) {
      throw new Error('Failed to update printer');
    }
    return response.data;
  },

  /**
   * Remove a printer from workspace
   */
  remove: async (id: string): Promise<void> => {
    const response = await api.delete<void>(`/printers/${id}`);
    if (!response.success) {
      throw new Error('Failed to remove printer');
    }
  },

  // --- NESTED: Accessories ---

  /**
   * List accessories for a printer
   */
  listAccessories: async (printerId: string): Promise<TenantPrinterAccessoryWithDetails[]> => {
    const response = await api.get<TenantPrinterAccessoryWithDetails[]>(
      `/printers/${printerId}/accessories`
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch accessories');
    }
    return response.data;
  },

  /**
   * Add an accessory to a printer
   */
  addAccessory: async (
    printerId: string,
    input: AddAccessoryToPrinterInput
  ): Promise<TenantPrinterAccessoryWithDetails> => {
    const response = await api.post<TenantPrinterAccessoryWithDetails>(
      `/printers/${printerId}/accessories`,
      input
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to add accessory');
    }
    return response.data;
  },

  /**
   * Update an accessory
   */
  updateAccessory: async (
    printerId: string,
    accessoryId: string,
    input: UpdateAccessoryInput
  ): Promise<TenantPrinterAccessoryWithDetails> => {
    const response = await api.patch<TenantPrinterAccessoryWithDetails>(
      `/printers/${printerId}/accessories/${accessoryId}`,
      input
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to update accessory');
    }
    return response.data;
  },

  /**
   * Remove an accessory from a printer
   */
  removeAccessory: async (printerId: string, accessoryId: string): Promise<void> => {
    const response = await api.delete<void>(
      `/printers/${printerId}/accessories/${accessoryId}`
    );
    if (!response.success) {
      throw new Error('Failed to remove accessory');
    }
  },

  // --- NESTED: Notes ---

  /**
   * List notes for a printer
   */
  listNotes: async (printerId: string): Promise<TenantPrinterNote[]> => {
    const response = await api.get<TenantPrinterNote[]>(`/printers/${printerId}/notes`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch notes');
    }
    return response.data;
  },

  /**
   * Create a note for a printer
   */
  createNote: async (printerId: string, input: CreatePrinterNoteInput): Promise<TenantPrinterNote> => {
    const response = await api.post<TenantPrinterNote>(
      `/printers/${printerId}/notes`,
      input
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to create note');
    }
    return response.data;
  },

  /**
   * Update a note
   */
  updateNote: async (
    printerId: string,
    noteId: string,
    input: UpdatePrinterNoteInput
  ): Promise<TenantPrinterNote> => {
    const response = await api.patch<TenantPrinterNote>(
      `/printers/${printerId}/notes/${noteId}`,
      input
    );
    if (!response.success || !response.data) {
      throw new Error('Failed to update note');
    }
    return response.data;
  },

  /**
   * Delete a note
   */
  deleteNote: async (printerId: string, noteId: string): Promise<void> => {
    const response = await api.delete<void>(
      `/printers/${printerId}/notes/${noteId}`
    );
    if (!response.success) {
      throw new Error('Failed to delete note');
    }
  },
};

// ============================================================================
// PRINTER LOOKUP API (Brands, Models, Accessory Types)
// ============================================================================

export const printerLookupApi = {
  // --- Brands ---

  /**
   * List all printer brands
   */
  listBrands: async (): Promise<PrinterBrand[]> => {
    const response = await api.get<PrinterBrand[]>('/printer-brands');
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch printer brands');
    }
    return response.data;
  },

  /**
   * Create a custom printer brand
   */
  createBrand: async (input: CreatePrinterBrandInput): Promise<PrinterBrand> => {
    const response = await api.post<PrinterBrand>('/printer-brands', input);
    if (!response.success || !response.data) {
      throw new Error('Failed to create brand');
    }
    return response.data;
  },

  /**
   * Delete a custom printer brand
   */
  deleteBrand: async (id: string): Promise<void> => {
    const response = await api.delete<void>(`/printer-brands/${id}`);
    if (!response.success) {
      throw new Error('Failed to delete brand');
    }
  },

  // --- Models ---

  /**
   * List all printer models (optionally filtered by brand)
   */
  listModels: async (brandId?: string): Promise<PrinterModel[]> => {
    const response = await api.get<PrinterModel[]>(`/printer-models${buildQueryString(brandId ? { brandId } : undefined)}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch printer models');
    }
    return response.data;
  },

  /**
   * Create a custom printer model
   */
  createModel: async (input: CreatePrinterModelInput): Promise<PrinterModel> => {
    const response = await api.post<PrinterModel>('/printer-models', input);
    if (!response.success || !response.data) {
      throw new Error('Failed to create model');
    }
    return response.data;
  },

  /**
   * Delete a custom printer model
   */
  deleteModel: async (id: string): Promise<void> => {
    const response = await api.delete<void>(`/printer-models/${id}`);
    if (!response.success) {
      throw new Error('Failed to delete model');
    }
  },

  // --- Accessory Types ---

  /**
   * List all accessory types
   */
  listAccessoryTypes: async (): Promise<AccessoryType[]> => {
    const response = await api.get<AccessoryType[]>('/accessory-types');
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch accessory types');
    }
    return response.data;
  },

  /**
   * Create a custom accessory type
   */
  createAccessoryType: async (input: CreateAccessoryTypeInput): Promise<AccessoryType> => {
    const response = await api.post<AccessoryType>('/accessory-types', input);
    if (!response.success || !response.data) {
      throw new Error('Failed to create accessory type');
    }
    return response.data;
  },

  /**
   * Delete a custom accessory type
   */
  deleteAccessoryType: async (id: string): Promise<void> => {
    const response = await api.delete<void>(`/accessory-types/${id}`);
    if (!response.success) {
      throw new Error('Failed to delete accessory type');
    }
  },
};

// ============================================================================
// PRINT API (Print Job Operations)
// ============================================================================

export const printApi = {
  /**
   * List print jobs (optionally filtered)
   */
  list: async (query?: PrintQuery): Promise<PrintWithRelations[]> => {
    const response = await api.get<PrintWithRelations[]>(`/prints${buildQueryString(query)}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch prints');
    }
    return response.data;
  },

  /**
   * Get a single print job
   */
  getById: async (id: string): Promise<PrintWithRelations> => {
    const response = await api.get<PrintWithRelations>(`/prints/${id}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch print');
    }
    return response.data;
  },

  /**
   * Create a print job
   */
  create: async (input: CreatePrintInput): Promise<PrintWithRelations> => {
    const response = await api.post<PrintWithRelations>('/prints', input);
    if (!response.success || !response.data) {
      throw new Error('Failed to create print');
    }
    return response.data;
  },

  /**
   * Update a print job
   */
  update: async (id: string, input: UpdatePrintInput): Promise<PrintWithRelations> => {
    const response = await api.patch<PrintWithRelations>(`/prints/${id}`, input);
    if (!response.success || !response.data) {
      throw new Error('Failed to update print');
    }
    return response.data;
  },

  /**
   * Delete a print job
   */
  delete: async (id: string): Promise<void> => {
    const response = await api.delete<void>(`/prints/${id}`);
    if (!response.success) {
      throw new Error('Failed to delete print');
    }
  },
};

// Legacy export for backward compatibility
export const printerApi = tenantPrinterApi;

// Re-export types for convenience
export type { TenantPrinterQuery, PrinterLibraryQuery, PrintQuery };
